import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Image as ImageIcon, Plus, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { AZ, loadOverrides } from "../i18n";
import { founder } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAM_KEY = "json:about_team_members";
const field = "w-full rounded-lg border border-white/15 bg-surface px-3 py-2.5 text-sm text-white outline-none focus:border-gold";

const makeId = () => `member-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const normalizeMember = (member = {}) => ({
  id: member.id || makeId(),
  name: typeof member.name === "object" && member.name ? { en: member.name.en || "", az: member.name.az || "" } : { en: member.name || "", az: "" },
  role: typeof member.role === "object" && member.role ? { en: member.role.en || "", az: member.role.az || "" } : { en: member.role || "", az: "" },
  bio: typeof member.bio === "object" && member.bio ? { en: member.bio.en || "", az: member.bio.az || "" } : { en: member.bio || "", az: "" },
  fullBio: typeof member.fullBio === "object" && member.fullBio ? { en: member.fullBio.en || "", az: member.fullBio.az || "" } : { en: member.fullBio || "", az: "" },
  photo: typeof member.photo === "string" ? member.photo : "",
  email: typeof member.email === "string" ? member.email : "",
  linkedin: typeof member.linkedin === "string" ? member.linkedin : "",
  published: member.published === true,
});

const legacyFounder = (overrides) => {
  const value = (key, fallback, lang) => overrides[key]?.[lang] || fallback;
  return normalizeMember({
    id: "founder",
    name: {
      en: value("text:founder_name", founder.name, "en"),
      az: value("text:founder_name", AZ[founder.name] || founder.name, "az"),
    },
    role: {
      en: value("text:founder_role", founder.role, "en"),
      az: value("text:founder_role", AZ[founder.role] || founder.role, "az"),
    },
    bio: {
      en: value("text:founder_bio_1", founder.bio[0], "en"),
      az: value("text:founder_bio_1", AZ[founder.bio[0]] || founder.bio[0], "az"),
    },
    photo: value("image:founder_photo", founder.photo, "en"),
    published: true,
  });
};

async function saveEntry(key, en, az) {
  const response = await fetch(`${API}/admin/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ key, value: { en, az } }),
  });
  if (!response.ok) throw new Error("Dəyişiklik yadda saxlanmadı.");
}

export function AboutAdmin() {
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    loadOverrides(true).then((overrides) => {
      const saved = overrides[TEAM_KEY]?.en || overrides[TEAM_KEY]?.az;
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setMembers(parsed.map(normalizeMember));
            return;
          }
        } catch {}
      }
      setMembers([legacyFounder(overrides)]);
    });
  }, []);

  const updateMember = (id, fieldName, lang, value) => {
    setMembers((current) => current.map((member) => member.id === id
      ? { ...member, [fieldName]: { ...member[fieldName], [lang]: value } }
      : member));
  };

  const upload = async (id, file) => {
    setUploading(id);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(`${API}/admin/media`, { method: "POST", body: form, credentials: "include" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Şəkil yüklənmədi.");
      setMembers((current) => current.map((member) => member.id === id ? { ...member, photo: result.url } : member));
      toast.success("Şəkil yükləndi. Dəyişikliyi saxlamağı unutmayın.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(null);
    }
  };

  const moveMember = (index, direction) => {
    setMembers((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const encode = (lang) => JSON.stringify(members.map((member) => ({
        id: member.id,
        name: member.name,
        role: member.role,
        bio: member.bio,
        fullBio: member.fullBio,
        photo: member.photo,
        email: member.email,
        linkedin: member.linkedin,
        published: member.published,
      })));
      const en = encode("en");
      const az = encode("az");
      if (en.length > 16000 || az.length > 16000) throw new Error("Komanda məlumatları 16000 simvoldan qısa olmalıdır.");
      await saveEntry(TEAM_KEY, en, az);
      await loadOverrides(true);
      toast.success("Komanda məlumatları yadda saxlandı.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10 border border-white/10 bg-panel p-6 sm:p-8" data-testid="about-admin">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[.2em] text-gold"><ImageIcon className="h-4 w-4" /> Haqqımızda</p>
          <h2 className="mt-3 font-serif text-2xl text-white">Əməkdaşları idarə et</h2>
          <p className="mt-2 max-w-2xl text-sm text-mist">İctimai səhifədə yayımlanan profillər foto, ad və vəzifə ilə göstərilir. Bioqrafiya və əlaqə məlumatları istəyə bağlıdır.</p>
        </div>
        <button type="button" onClick={() => setMembers((current) => [...current, normalizeMember({})])} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:border-gold">
          <Plus className="h-4 w-4" /> Əməkdaş əlavə et
        </button>
      </div>

      <div className="mt-7 space-y-6">
        {members.map((member, index) => (
          <article key={member.id} className="border border-white/10 p-4 sm:p-5" data-testid={`about-admin-member-${index}`}>
            <div className="grid gap-6 lg:grid-cols-[200px_1fr]">
              <div>
                <div className="flex aspect-[4/5] items-center justify-center overflow-hidden bg-white/5">
                  {member.photo ? <img src={member.photo} alt="" className="h-full w-full object-contain" /> : <span className="px-4 text-center text-xs text-white/55">Foto əlavə edilməyib</span>}
                </div>
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:border-gold">
                  <Upload className="h-4 w-4" /> {uploading === member.id ? "Yüklənir…" : "Foto yüklə"}
                  <input className="sr-only" type="file" accept="image/*" disabled={uploading === member.id} onChange={(event) => { const file = event.target.files?.[0]; if (file) upload(member.id, file); event.target.value = ""; }} />
                </label>
              </div>
              <div>
                <div className="grid gap-5 xl:grid-cols-2">
                  {[ ["en", "English"], ["az", "Azərbaycan dili"] ].map(([lang, label]) => (
                    <div key={lang} className="space-y-3">
                      <p className="font-mono text-[10px] uppercase tracking-[.18em] text-gold">{label}</p>
                      <label className="block text-xs text-white/65">Ad və soyad<input value={member.name[lang]} onChange={(event) => updateMember(member.id, "name", lang, event.target.value)} className={`${field} mt-1`} /></label>
                      <label className="block text-xs text-white/65">Vəzifə<input value={member.role[lang]} onChange={(event) => updateMember(member.id, "role", lang, event.target.value)} className={`${field} mt-1`} /></label>
                      <label className="block text-xs text-white/65">Qısa təqdimat<textarea rows="3" value={member.bio[lang]} onChange={(event) => updateMember(member.id, "bio", lang, event.target.value)} className={`${field} mt-1`} /></label>
                      <label className="block text-xs text-white/65">Ətraflı bioqrafiya<textarea rows="5" value={member.fullBio[lang]} onChange={(event) => updateMember(member.id, "fullBio", lang, event.target.value)} className={`${field} mt-1`} /></label>
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid gap-5 border-t border-white/10 pt-4 sm:grid-cols-2">
                  <label className="block text-xs text-white/65">Peşəkar e-poçt<input type="email" value={member.email} onChange={(event) => setMembers((current) => current.map((item) => item.id === member.id ? { ...item, email: event.target.value } : item))} className={`${field} mt-1`} /></label>
                  <label className="block text-xs text-white/65">LinkedIn keçidi<input type="url" value={member.linkedin} onChange={(event) => setMembers((current) => current.map((item) => item.id === member.id ? { ...item, linkedin: event.target.value } : item))} className={`${field} mt-1`} /></label>
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
                  <label className="inline-flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={member.published} onChange={(event) => setMembers((current) => current.map((item) => item.id === member.id ? { ...item, published: event.target.checked } : item))} /> Yayımla</label>
                  <div className="flex items-center gap-2">
                    <button type="button" title="Yuxarı daşı" aria-label="Yuxarı daşı" disabled={index === 0} onClick={() => moveMember(index, -1)} className="rounded border border-white/20 p-2 text-white disabled:opacity-35"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" title="Aşağı daşı" aria-label="Aşağı daşı" disabled={index === members.length - 1} onClick={() => moveMember(index, 1)} className="rounded border border-white/20 p-2 text-white disabled:opacity-35"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setMembers((current) => current.filter((item) => item.id !== member.id))} className="inline-flex items-center gap-2 rounded-full border border-red-300/30 px-3 py-2 text-xs font-semibold text-red-200"><Trash2 className="h-4 w-4" /> Sil</button>
                  </div>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <button type="button" disabled={busy} onClick={save} className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink disabled:opacity-50">
        <Save className="h-4 w-4" /> {busy ? "Yadda saxlanır…" : "Komandanı yadda saxla"}
      </button>
    </section>
  );
}